import React, { useState, useEffect } from 'react';
import SectionLast from '../../components/sectionlast/SectionLast';
import { BASE_URL } from '../../../../utils/config';
import { Link } from 'react-router-dom';

function BlogPage() {
    const [blogs, setBlogs] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const blogsPerPage = 6;

    useEffect(() => {
        const fetchBlogs = async () => {
            setLoading(true);
            setError(null);

            try {
                const res = await fetch(`${BASE_URL}blogs`);
                if (!res.ok) throw new Error('Failed to fetch blogs');

                const data = await res.json();
                setBlogs(data.data || []);
            } catch (err) {
                console.error("API error:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchBlogs();
    }, []);

    const totalPages = Math.ceil(blogs.length / blogsPerPage);
    const startIndex = (currentPage - 1) * blogsPerPage;
    const endIndex = startIndex + blogsPerPage;

    const currentBlogs = blogs.slice(startIndex, endIndex);
    const featuredBlog = blogs[0];
    const gridBlogs = currentBlogs.filter(blog => blog.id !== featuredBlog?.id);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 400, behavior: 'smooth' });
    };

    return (
        <div>
            {/* ==================== HERO SECTION ==================== */}
            <section className="bg-[#1b152b] relative overflow-hidden">
                {/* Dot Background Pattern */}
                <div className="absolute inset-0 opacity-30 pointer-events-none">
                    <div
                        className="absolute inset-0"
                        style={{
                            backgroundImage: `radial-gradient(circle, #6b5b95 1px, transparent 1px)`,
                            backgroundSize: '40px 40px',
                            backgroundPosition: 'center',
                        }}
                    />
                </div>

                <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-24 sm:pt-28 pb-10 sm:pb-14">
                    {/* Title + Description */}
                    <div className="max-w-2xl">
                        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-tight">
                            The Element Us Labs Blog
                        </h1>
                        <p className="mt-4 sm:mt-6 text-base sm:text-lg text-gray-300 max-w-lg mb-4 leading-relaxed">
                            Our team delivers the latest blockchain insights and analysis leveraging the most comprehensive proprietary dataset in the market.
                        </p>
                    </div>

                    {/* Dynamic Featured Article */}
                    {loading ? (
                        <div className="mt-8 h-64 sm:h-96 lg:h-[520px] bg-gray-800 rounded-2xl sm:rounded-3xl flex items-center justify-center">
                            <p className="text-gray-400 text-sm sm:text-base">Loading featured article...</p>
                        </div>
                    ) : error ? (
                        <div className="mt-8 h-64 sm:h-96 lg:h-[520px] bg-red-900/20 rounded-2xl sm:rounded-3xl flex items-center justify-center">
                            <p className="text-red-400 text-sm sm:text-base">Failed to load featured article</p>
                        </div>
                    ) : featuredBlog ? (
                        <div className="mt-8 relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
                            <img
                                src={featuredBlog.image}
                                alt={featuredBlog.title}
                                className="w-full h-56 sm:h-80 md:h-[420px] lg:h-[520px] object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/90" />

                            <div className="absolute top-4 sm:top-6 left-4 sm:left-6">
                                <span className="bg-white text-[#0f0a2e] text-xs font-semibold px-3 sm:px-4 py-1.5 sm:py-2 rounded">
                                    ARTICLE
                                </span>
                            </div>

                            <div className="absolute bottom-0 left-0 right-0 p-2 sm:p-8 md:p-12">
                                <p className="text-xs sm:text-sm text-purple-500 mb-2 sm:mb-3">
                                    {new Date(featuredBlog.created_at).toLocaleDateString('en-US', {
                                        month: 'long',
                                        day: 'numeric',
                                        year: 'numeric'
                                    })}
                                </p>
                                <h2 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-semibold leading-tight text-white line-clamp-3">
                                    {featuredBlog.title}
                                </h2>
                                <a
                                    href={`/blog/${featuredBlog.id}`}
                                    className="mt-4 sm:mt-6 inline-block text-white border-b border-white pb-1 hover:text-purple-300 hover:border-purple-300 transition-colors text-sm sm:text-base"
                                >
                                    Read more
                                </a>
                            </div>
                        </div>
                    ) : null}
                </div>
            </section>

            {/* ==================== BLOG GRID SECTION ==================== */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-14 lg:py-16">
                {error && (
                    <div className="text-center py-16 sm:py-20 text-red-400 text-sm sm:text-base">
                        Error: {error}
                    </div>
                )}

                {!loading && !error && (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
                            {gridBlogs.length > 0 ? (
                                gridBlogs.map((blog) => (
                                    <Link to={`/blog/${blog.id}`} key={blog.id} className="group">
                                        <div className="relative rounded-xl overflow-hidden mb-3">
                                            <img
                                                src={blog.image}
                                                alt={blog.title}
                                                className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                            <div className="absolute bottom-3 left-3 sm:bottom-4 sm:left-4">
                                                <span className="bg-white/95 text-[#0f0a2e] text-xs font-semibold px-4 sm:px-5 py-1 sm:py-1.5 rounded-full">
                                                    BLOG
                                                </span>
                                            </div>
                                        </div>
                                        <h3 className="text-black text-base sm:text-[18px] lg:text-[21px] leading-snug font-semibold line-clamp-3">
                                            {blog.title}
                                        </h3>
                                        <p className="text-xs sm:text-sm text-gray-400 mt-1.5 sm:mt-2">
                                            {new Date(blog.created_at).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })}
                                        </p>
                                    </Link>
                                ))
                            ) : (
                                <p className="col-span-full text-center text-gray-400 py-10 text-sm sm:text-base">
                                    No blogs available at the moment.
                                </p>
                            )}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex justify-center items-center gap-2 sm:gap-4 mt-14 sm:mt-20">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center border border-gray-300 rounded-full hover:bg-gray-100 disabled:opacity-50 transition-colors"
                                >
                                    <span className="text-[#8932f6] text-base sm:text-lg font-medium">←</span>
                                </button>

                                <div className="flex gap-1 sm:gap-2">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                        <button
                                            key={page}
                                            onClick={() => handlePageChange(page)}
                                            className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full text-xs sm:text-sm font-medium transition-all ${
                                                page === currentPage
                                                    ? 'bg-[#0f0a2e] text-white'
                                                    : 'hover:bg-gray-100 text-gray-700'
                                            }`}
                                        >
                                            {page}
                                        </button>
                                    ))}
                                </div>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center border border-gray-300 rounded-full hover:bg-gray-100 disabled:opacity-50 transition-colors"
                                >
                                    <span className="text-[#8932f6] text-base sm:text-lg font-medium">→</span>
                                </button>
                            </div>
                        )}
                    </>
                )}

                {loading && (
                    <div className="text-center py-16 sm:py-20 text-gray-400 text-sm sm:text-base">
                        Loading blogs...
                    </div>
                )}
            </main>

            <SectionLast heading={'Get started with Element Us Labs'} />
        </div>
    );
}

export default BlogPage;